import {
  Alert,
  Card,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconPackage,
  IconSparkles,
  IconUsers,
} from '@tabler/icons-react';
import { useApi } from '../../api';
import { formatCents } from '../products/productDisplay';
import RatingStars from '../sellers/RatingStars';

// Seller analytics section (contract §4.12) — GET /api/v1/me/seller/stats.
//
// Rendered prominently on the seller dashboard. Same tile language as the
// dashboard's StatBox row (Paper withBorder, dimmed xs label, xl bold value)
// and AdminStats' grouped cards (icon + title header per group).

const StatTile = ({ label, value, children }) => (
  <Paper withBorder p="md" radius="md">
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      {children ?? <Text size="xl" fw={700}>{value}</Text>}
    </Stack>
  </Paper>
);

const StatGroup = ({ title, icon, cols, children }) => (
  <Card withBorder radius="md" padding="md">
    <Group gap="xs" mb="sm">
      <ThemeIcon size="sm" color="grape" variant="light" radius="xl">
        {icon}
      </ThemeIcon>
      <Text fw={600} size="sm">{title}</Text>
    </Group>
    <SimpleGrid cols={cols} spacing="sm">
      {children}
    </SimpleGrid>
  </Card>
);

const GROUPS = [
  { title: 'Audience', icon: <IconUsers size="0.8rem" />, cols: { base: 1, xs: 3 }, tiles: 3 },
  { title: 'Content', icon: <IconPackage size="0.8rem" />, cols: { base: 1, xs: 3 }, tiles: 3 },
  { title: 'Activity', icon: <IconSparkles size="0.8rem" />, cols: { base: 2, sm: 4 }, tiles: 5 },
];

const SellerStatsSkeleton = () => (
  <Stack gap="lg" data-testid="seller-stats-loading">
    {GROUPS.map((group) => (
      <StatGroup key={group.title} title={group.title} icon={group.icon} cols={group.cols}>
        {Array.from({ length: group.tiles }, (_, i) => (
          <Skeleton key={i} height={74} radius="md" />
        ))}
      </StatGroup>
    ))}
  </Stack>
);

const SellerStats = () => {
  const { data: stats, loading, error } = useApi('/api/v1/me/seller/stats');

  if (loading) return <SellerStatsSkeleton />;

  if (error) {
    return (
      <Alert
        icon={<IconExclamationCircle size="1rem" />}
        color="red"
        title="Error loading analytics"
      >
        {error.message}
      </Alert>
    );
  }

  if (!stats) return null;

  return (
    <Stack gap="lg">
      <StatGroup title="Audience" icon={GROUPS[0].icon} cols={GROUPS[0].cols}>
        <StatTile label="Profile Views" value={stats.profile_views ?? 0} />
        <StatTile label="Favorites" value={stats.favorites_count ?? 0} />
        <StatTile label="Rating">
          {stats.rating_avg == null ? (
            <Text size="sm" c="dimmed">No ratings yet</Text>
          ) : (
            <RatingStars value={stats.rating_avg} count={stats.rating_count} size="md" />
          )}
        </StatTile>
      </StatGroup>

      <StatGroup title="Content" icon={GROUPS[1].icon} cols={GROUPS[1].cols}>
        <StatTile
          label="Products (active / total)"
          value={`${stats.products_active ?? 0} / ${stats.products_total ?? 0}`}
        />
        <StatTile
          label="Media (public / total)"
          value={`${stats.media_public ?? 0} / ${stats.media_total ?? 0}`}
        />
        <StatTile label="Product Views" value={stats.product_views ?? 0} />
      </StatGroup>

      <StatGroup title="Activity" icon={GROUPS[2].icon} cols={GROUPS[2].cols}>
        <StatTile label="Messages (30 days)" value={stats.messages_received_30d ?? 0} />
        <StatTile label="Requests" value={stats.requests_total ?? 0} />
        <StatTile label="Pending Requests" value={stats.requests_pending ?? 0} />
        <StatTile label="Accepted Requests" value={stats.requests_accepted ?? 0} />
        <StatTile label="Tips Received" value={formatCents(stats.tips_received_cents ?? 0)} />
      </StatGroup>
    </Stack>
  );
};

export default SellerStats;
